// Phase 2.6 — full EPG store. Supersedes the Phase 2.5 placeholder
// `useEpgInlineStore` by owning the entire programme catalog (not just the
// inline now-playing line): channel index, programme list, indexed lookups
// by `tvg-id` with channel-name fallback, _nowPlayingCache keyed by
// `programmes.length`, and the 5-minute background refresh that the Guide
// page wires to its mount/unmount lifecycle.
//
// Source: legacy `public/js/components/EpgGuide.js:173-186` (background
// refresh), 210-307 (loadEpg + fetchEpgData), 287-295 (channel index by
// id + name.toLowerCase()), 308-388 (getCurrentProgram / getProgramAt),
// 420-440 (_nowPlayingCache keyed by programmes.length).
//
// Server: `GET /api/proxy/epg/:sourceId` — hardcoded ±24h window per
// `server/routes/proxy.js:1162-1256`. The store iterates all enabled
// `xtream`/`epg` sources in parallel and dedupes channels by
// `(sourceId, tvgId)` (`[NEW]` over legacy concat).

import { defineStore } from 'pinia';
import { ref, shallowRef, markRaw, type Ref, type ShallowRef } from 'vue';
import {
    api,
    type EpgChannel,
    type Programme,
    type EpgGetResponse,
    type EpgWindowResponse,
} from '@/lib/api';
import { log } from '@/lib/log';
import {
    normalizeChannelName,
    extractCountryPrefix,
    normalizeChannelNameWithoutPrefix,
} from '@/lib/epgMatch';
import { expandPrefixAliases } from '@/lib/epgAliases';

/** EPG channel row enriched with the source it came from (multi-source merge). */
export interface EpgChannelWithSource extends EpgChannel {
    sourceId: number;
    /**
     * Optional back-reference to the live channel whose row this represents.
     * Set by `Guide.vue` when building synthetic rows so a right-click on
     * the channel area can resolve to the original `useChannelListStore`
     * `Channel` (the only thing `<EpgMappingPicker>` accepts as a prop).
     * Undefined for non-Guide consumers (e.g. `<EpgMappingPicker>`'s own
     * full-catalogue list).
     */
    liveItemId?: string;
    liveSourceId?: number;
}

/** Programme row enriched with the source it came from + pre-parsed timestamps. */
export interface ProgrammeWithSource extends Programme {
    sourceId: number;
    /** Composite live id (`sourceId:itemId`) when loaded through the window API. */
    liveId?: string;
    /** Resolved EPG source id for a live-window programme. */
    epgSourceId?: number;
    /** Resolved XMLTV channel id for a live-window programme. */
    epgChannelId?: string;
    /** Parsed `start` ISO → epoch ms (computed once on load to avoid Date parsing in the hot loop). */
    startMs: number;
    /** Parsed `stop` ISO → epoch ms. */
    stopMs: number;
}

/** Refresh interval for the background EPG poller (legacy `EpgGuide.js:173-186`). */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Guide-row-status Batch 2 — the render-layer status of one Guide row's
 * fetch, derived from `isGuideChannelLoaded` / `isGuideChannelFailed` /
 * `hasGuideEpgForLiveId`. `'loaded'` covers BOTH the confirmed-empty and
 * populated cases — `<EpgGuideRowStatus>` splits those further using the
 * row's own `programmes` (already available at the render site), so this
 * type only carries the fetch-lifecycle distinction the store actually owns.
 */
export type GuideChannelStatus = 'loading' | 'loaded' | 'failed';

export interface EpgWindowLoad {
    liveIds: string[];
    startMs: number;
    endMs: number;
    force?: boolean;
}

/**
 * Last window payload `loadWindow` ran with. Stored module-level so the
 * background refresh poller can re-run the same shape every 5 min instead of
 * silently reverting to the unfiltered `load()` shape (which would re-blast
 * the full 50k-channel catalogue at every tick on large playlists).
 *
 * Reset between Vitest cases via `_resetEpgStoreFilterForTests` (Phase 2.6
 * retro #5 — every module-level singleton in this codebase needs an afterEach
 * drain in its consumers' test files).
 */
let _lastWindow: EpgWindowLoad | null = null;
/**
 * Stale-time map keyed by `_windowHash(opts)`. A window change always
 * re-fetches because its hash isn't in the map; a repeat call with the same
 * opts hits the guard within `REFRESH_INTERVAL_MS`.
 */
const _filterStaleAt = new Map<string, number>();

function _windowHash(opts: EpgWindowLoad | null | undefined): string {
    if (!opts) return '__none__';
    return JSON.stringify({
        liveIds: [...new Set(opts.liveIds || [])].sort(),
        startMs: Math.floor(Number(opts.startMs) || 0),
        endMs: Math.floor(Number(opts.endMs) || 0),
    });
}

/**
 * Test helper — drain the module-level window/stale-time memo. Call from
 * `afterEach` in any test file that exercises `loadWindow` or background
 * refresh.
 */
export function _resetEpgStoreFilterForTests(): void {
    _lastWindow = null;
    _filterStaleAt.clear();
}

// ---------------------------------------------------------------------------
// Guide-scoped accumulating window — module-level singletons (Phase 1 §A D3)
// ---------------------------------------------------------------------------
//
// These are SEPARATE from `_lastWindow` / `windowChannels` / `windowProgrammes`
// which belong to Live's single-channel tune-in path. The Guide gets its own
// namespace so a Live tune-in NEVER stomps the Guide's accumulation and vice-
// versa. (This is the deliberate per-consumer scope contract the post-4.10
// retro mandates — see CLAUDE.md rule #3.)
//

/**
 * Batch 1 perf fix — the Guide's Prev/Next nav step (`EpgFilters.vue`'s
 * `nudge(-2)` / `nudge(2)` buttons, applied in `Guide.vue`'s
 * `onTimeOffsetChange`). Exported so `Guide.vue` derives its own required-
 * window margin from THIS value instead of hardcoding a second "2h" that
 * could silently drift out of sync with the nav buttons (post-4.10 retro
 * rule: two numbers describing one fact must be derived from each other).
 * `setGuideWindow`'s fetch padding below is ALSO derived from it — the
 * containment check's guarantee ("a single nav press never re-triggers a
 * fetch") is a mathematical property of padding by at least the nav step,
 * not a coincidence of both happening to be 2h.
 */
export const GUIDE_WINDOW_NAV_STEP_MS = 2 * 60 * 60 * 1000;
/**
 * Padding added to each side of the window `setGuideWindow`'s caller
 * requests before it is actually fetched — see that function's doc comment.
 */
const GUIDE_WINDOW_FETCH_PAD_MS = GUIDE_WINDOW_NAV_STEP_MS;
/**
 * R1 review Blocker 4 (2) — single source of truth for the guide fetch's
 * per-request id cap, shared with `Guide.vue` (which slices its own
 * visible-ids batches to this before calling `ensureGuideChannels`) so the
 * two never carry independent "400" literals that could drift out of sync.
 * Chosen well under the backend's hard `MAX_LIVE_IDS` (2000,
 * `server/services/epgWindowQuery.js`) — see Guide.vue's own doc comment on
 * its `fetchVisibleChannelIds` for the measured payload-size rationale.
 * `_extendGuideWindow` below reuses it to chunk `_guideLoadedLiveIds`
 * (which — unlike every OTHER caller of `ensureGuideChannels` — accumulates
 * across an entire session and is never pre-sliced by a caller) instead of
 * sending it as one unbounded request.
 */
export const GUIDE_FETCH_CAP = 400;
/**
 * R1 review Blocker 3 — the cap on how wide the guide's accumulated fetched
 * span may grow via UNION (see `setGuideWindow`) before it falls back to a
 * hard clear-and-refetch. Chosen as one calendar day: it is the widest span
 * this store EVER fetched before this batch (the flat 24h `VIEWPORT_WINDOW_MS`
 * `Guide.vue` used to send on every viewport change), so accepting an
 * accumulation up to that size over a scrubbing session is, at worst, a
 * return to the old cost ceiling — never worse — while a hard bound at all
 * prevents literally unbounded growth from a long session of continuous
 * horizontal scrolling in one direction.
 */
const GUIDE_WINDOW_MAX_SPAN_MS = 24 * 60 * 60 * 1000;

/** True if two closed intervals share at least one point (touching counts). */
function _spansOverlap(
    a: { startMs: number; endMs: number },
    b: { startMs: number; endMs: number },
): boolean {
    return a.startMs <= b.endMs && b.startMs <= a.endMs;
}
//
// All mutable singletons below are drained by `_resetGuideWindowForTests()`.
let _guideWindow: { startMs: number; endMs: number } | null = null;
/**
 * Batch 1 perf fix — the span of time actually covered by the last real
 * fetch, tracked EXPLICITLY rather than inferred from `_guideWindow` (which
 * also drives the literal API call params in `ensureGuideChannels` /
 * `startBackgroundRefresh` and is kept in sync with this, but conflating
 * "what I last asked the API for" with "what setGuideWindow should treat as
 * already covered" is exactly the ambiguity a future edit to either call
 * site could silently break). See `setGuideWindow`'s containment check.
 */
let _guideFetchedSpan: { startMs: number; endMs: number } | null = null;
const _guideLoadedLiveIds = new Set<string>();
/** Pending liveIds waiting for the next microtask flush (coalescing). */
let _guidePendingIds: Set<string> = new Set();
let _guideFlushPromise: Promise<void> | null = null;
/** Sequence counter — guards against out-of-order responses. */
let _guideSeq = 0;
/**
 * Batch 1 perf fix — persistent dedupe keys for `_mergeGuidePayload`,
 * maintained incrementally instead of rebuilt by scanning the full
 * `guideChannels` / `guideProgrammes` accumulation on every merge (that scan
 * was O(total accumulated) on EVERY scroll-triggered batch — see that
 * function's doc comment). Module-level (not closure-scoped inside
 * `useEpgStore`) so `_resetGuideWindowForTests()` can drain them the same
 * way it drains every other guide-window singleton — a stale entry here
 * would silently make `_mergeGuidePayload` treat a legitimately new
 * post-clear row as already seen and drop it.
 */
let _guideSeenChannelKeys = new Set<string>();
let _guideSeenProgrammeKeys = new Set<string>();

/**
 * Guide-row-status Batch 2 — ids whose MOST RECENT `ensureGuideChannels`
 * fetch attempt errored and got rolled back (see the flush loop's `catch`
 * below). Deliberately SEPARATE from `_guideLoadedLiveIds`: a failed id is
 * NOT in that set (the rollback removes it, so a later visibility trigger or
 * background refresh still retries it), but the render layer needs to tell
 * "never fetched yet" apart from "fetched and it errored" — before this,
 * both collapsed into the same infinite-shimmer look with no retry
 * affordance. Cleared for an id the moment a fetch is (re-)attempted for it
 * (see the "mark as loaded BEFORE the fetch" step) — a retry that is already
 * in flight should not still read as "failed" — and drained wholesale
 * alongside every other guide-window singleton in `_clearGuideState()` /
 * `_resetGuideWindowForTests()`.
 */
let _guideFailedLiveIds = new Set<string>();

/**
 * Test helper — drain Guide window module-level state. Call from `afterEach`
 * in any test file that exercises `ensureGuideChannels` / `setGuideWindow`.
 */
export function _resetGuideWindowForTests(): void {
    _guideWindow = null;
    _guideFetchedSpan = null;
    _guideLoadedLiveIds.clear();
    _guidePendingIds = new Set();
    _guideFlushPromise = null;
    _guideSeq = 0;
    _guideSeenChannelKeys = new Set();
    _guideSeenProgrammeKeys = new Set();
    _guideFailedLiveIds = new Set();
}

export const useEpgStore = defineStore('epg', () => {
    // shallowRef — Vue only tracks reassignment of the array reference; it
    // does NOT proxy every individual programme/channel object. With a
    // typical multi-source Xtream account this list runs to 100k+ entries;
    // deep reactivity over that many objects allocated 3+ GB and pegged
    // CPU on the Live page (every dependent computed walked every Proxy).
    // We replace the whole array on `load()` and never mutate items in
    // place, so shallow tracking is sufficient.
    /** Channels merged across enabled sources, deduped by `(sourceId, tvgId)`. */
    const channels: ShallowRef<EpgChannelWithSource[]> = shallowRef([]);
    /** Programme rows across all sources, with parsed start/stop ms. */
    const programmes: ShallowRef<ProgrammeWithSource[]> = shallowRef([]);
    /** Epoch ms of the last successful (full or refresh) load. */
    const lastLoadedAt = ref<number>(0);
    /** True while a `load(force?)` is in flight. */
    const isLoading = ref<boolean>(false);
    /** Per-source fetch sequence guard — concurrent loads from different callers are serialised by sequence. */
    const _loadSeq = ref<number>(0);

    // Indexed-lookup caches. All three invalidated together via
    // `_invalidateEpgIndex` so stale entries can't be served after a refresh.
    //
    // Post-4.10 — the channel-name fallback splits into three keys:
    //
    //   `_byTvgId`        : `tvgId` → programmes (unchanged from Phase A).
    //   `_byPrefixedName` : `${PREFIX}:${bareKey}` → programmes, where
    //                       `PREFIX` is the channel's extracted country
    //                       prefix (e.g. `VIP`, `DE`, `SKYGO`) — built once
    //                       from the EPG-side channel list. Channels with
    //                       no country prefix bucket under `:${bareKey}`.
    //                       Used by tier-2 strict-prefix match and tier-2.5
    //                       alias-prefix expansion in `getProgramAt`.
    //   `_byBareName`     : `bareKey` → channels (NOT programmes — we count
    //                       distinct candidates BEFORE pulling programmes
    //                       so the N=1 uniqueness gate fires correctly).
    let _byTvgId = new Map<string, ProgrammeWithSource[]>();
    let _byPrefixedName = new Map<string, ProgrammeWithSource[]>();
    let _byBareName = new Map<string, EpgChannelWithSource[]>();
    /**
     * Key for `_nowPlayingCache`. Legacy used `programmes.length` as a cheap proxy
     * for "data version" — incrementing it forces a rebuild on the next call.
     */
    let _nowPlayingKey: number = -1;
    let _nowPlayingCache: Map<string, ProgrammeWithSource | null> = new Map();
    // `_byLiveId` is the live-id → window-programme index. Populated by
    // `applyWindowPayload`; drained by `_invalidateWindowIndex` and `reset`.
    // NOT populated from the shared `programmes` array.
    let _byLiveId = new Map<string, ProgrammeWithSource[]>();

    /**
     * Window-scoped state. Populated exclusively by `applyWindowPayload` /
     * `loadWindow`. NEVER written by `load()` — it is the sole owner of
     * `channels` / `programmes`.
     *
     * `_byLiveId` (liveId → ProgrammeWithSource[]) serves
     * `getCurrentProgramForLiveId` / `hasEpgForLiveId` and is rebuilt
     * exclusively here — it is NOT derived from the shared `programmes` array.
     */
    const windowChannels: ShallowRef<EpgChannelWithSource[]> = shallowRef([]);
    const windowProgrammes: ShallowRef<ProgrammeWithSource[]> = shallowRef([]);

    // -----------------------------------------------------------------------
    // Guide-scoped accumulating window (Phase 1 §A — D3 scope contract)
    // -----------------------------------------------------------------------
    //
    // Completely separate from the `window*` refs above (Live's exclusive
    // namespace). `guide*` refs accumulate as the user scrolls the Guide
    // viewport; a Live tune-in calling `loadWindow` never touches these refs.
    //
    // `guideChannels` / `guideProgrammes` use shallowRef for the same reason as
    // `windowChannels` / `windowProgrammes` — we replace the array reference on
    // each merge rather than mutating items in place.
    const guideChannels: ShallowRef<EpgChannelWithSource[]> = shallowRef([]);
    const guideProgrammes: ShallowRef<ProgrammeWithSource[]> = shallowRef([]);
    // `_guideByLiveId` mirrors `_byLiveId` but for the Guide's accumulation.
    // Plain Map — rebuilt incrementally on each `ensureGuideChannels` response.
    let _guideByLiveId = new Map<string, ProgrammeWithSource[]>();
    /**
     * Reactive touch for `_guideLoadedLiveIds` (a plain Set, not a ref).
     * `_mergeGuidePayload` only reassigns `guideChannels`/`guideProgrammes`
     * when a fetch ADDS rows — a batch that resolves to zero new
     * channels/programmes (every requested id confirmed to have no data)
     * marks those ids loaded in `_guideLoadedLiveIds` WITHOUT touching either
     * shallowRef, so `isGuideChannelLoaded`'s consumers (the D6/U1 fix's
     * `visibleGroupedChannels`) would never re-run for exactly the
     * "confirmed empty" case they exist to detect. Bumped on every settled
     * `ensureGuideChannels` flush (success or rolled-back failure).
     */
    const _guideLoadedVersion = ref<number>(0);

    /**
     * Phase X3 (ux-remediation, U1) — multi-day EPG-availability probe for
     * the Guide's day-nav strip. Deliberately INDEPENDENT of `guideChannels`
     * / `guideProgrammes` / `_guideWindow` above: those accumulate ONE
     * render-viewport's worth of data at a time and are cleared on every
     * time-nav change (pinned by the D3 scope-contract tests), so they can
     * never hold more than the currently-viewed window. The day strip needs
     * to know which CALENDAR DAYS have data *before* the user navigates to
     * them — `loadGuideHorizon` fetches a wider one-shot window (up to the
     * server's `MAX_WINDOW_MS` cap — `epgWindowQuery.js`) and the result
     * feeds `deriveDayList` (`components/epg/_dayNav.ts`) directly. A later
     * call REPLACES the array — this is a point-in-time probe, not an
     * accumulation, so it never needs the guide window's dedup/merge machinery.
     */
    const guideHorizonProgrammes: ShallowRef<ProgrammeWithSource[]> = shallowRef([]);

    /**
     * EPG start notifications (Spec 10) — the notifier's OWN scope.
     *
     * A FOURTH namespace, deliberately, per the post-4.10 scope-contract rule
     * (CLAUDE.md rule #3: "a second consumer that inherits whatever the last
     * consumer loaded is a latent bug"). The notifier's shape diverges from
     * every existing consumer:
     *
     *   - Live's `window*` refs hold ONE tuned channel (plus whatever the
     *     sidebar's visible rows ensured) over a ±hours span, and `loadWindow`
     *     stamps `_lastWindow`, which the 5-minute background poller re-runs.
     *     A notifier fetch through `loadWindow` would therefore REPLACE the
     *     poller's shape with "all favourites, next 10 minutes" and Live's
     *     own now/next window would stop being refreshed.
     *   - The Guide's refs hold the scrolled viewport and are cleared on every
     *     time-nav change, so they can be empty at any moment.
     *
     * The notifier needs "every favourite, the next 10 minutes" to be present
     * whether or not the user ever opens Live or the Guide. So it gets its own
     * ref, written only by `loadNotifierWindow`, which REPLACES the array
     * (a point-in-time plan input, like `loadGuideHorizon` — no accumulation,
     * so no merge/dedup machinery is needed here; the planner dedupes).
     */
    const notifierProgrammes: ShallowRef<ProgrammeWithSource[]> = shallowRef([]);
    /**
     * `liveId` → channel display name, from the SAME window payload.
     *
     * Separate from `notifierProgrammes` because the name is NOT on a
     * programme row and must not be faked onto one: the server's
     * `formatProgramme` (`server/services/epgWindowQuery.js`) emits a fully
     * enumerated literal with no name field, and the name lives only in
     * `payload.channels[].name`. Widening the shared `ProgrammeWithSource`
     * type to carry it would let every other consumer read a field that is
     * populated on exactly one code path.
     */
    const notifierChannelNames: ShallowRef<Map<string, string>> = shallowRef(new Map());

    /** Background refresh `setInterval` token. `null` when stopped. */
    let _refreshTimer: ReturnType<typeof setInterval> | null = null;

    function _invalidateEpgIndex(): void {
        _byTvgId = new Map();
        _byPrefixedName = new Map();
        _byBareName = new Map();
        _nowPlayingCache = new Map();
        _nowPlayingKey = -1;
    }

    function _invalidateWindowIndex(): void {
        _byLiveId = new Map();
    }

    /**
     * Build the three-key index used by the four-tier resolution model.
     * tier-1 uses `_byTvgId`; tier-2 + tier-2.5 use `_byPrefixedName`; tier-3
     * uses `_byBareName` to count distinct candidates BEFORE pulling
     * programmes (the N=1 uniqueness gate). The plan's correctness invariant
     * — different broadcasters share the same bare key across countries —
     * is the reason tier-2/2.5 are prefix-aware AND tier-3 demands N=1.
     */
    function _rebuildIndex(): void {
        // tier-1: tvgId index (unchanged from Phase A).
        for (const p of programmes.value) {
            const k = (p.channelId || '').trim();
            if (!k) continue;
            const byId = _byTvgId.get(k);
            if (byId) byId.push(p);
            else _byTvgId.set(k, [p]);
        }
        // Build channel-side index: for each EPG channel, derive
        // (prefix, bareKey) → programmes (via tvgId join) for tier-2/2.5,
        // and (bareKey) → [channels] for tier-3's N=1 gate.
        for (const ch of channels.value) {
            const bareKey = normalizeChannelNameWithoutPrefix(ch.name || '');
            if (!bareKey) continue;
            const prefix = extractCountryPrefix(ch.name || '') || '';
            const prefixedKey = `${prefix}:${bareKey}`;
            const programmesForCh = _byTvgId.get(ch.id);
            if (programmesForCh && programmesForCh.length > 0) {
                const prefixedBucket = _byPrefixedName.get(prefixedKey);
                if (prefixedBucket) prefixedBucket.push(...programmesForCh);
                else _byPrefixedName.set(prefixedKey, [...programmesForCh]);
            }
            // The bare-name index buckets channels regardless of whether
            // they currently have programmes — the N=1 gate fires on
            // channel-cardinality, and getProgramAt checks programme
            // coverage on the resolved single channel.
            const bareBucket = _byBareName.get(bareKey);
            if (bareBucket) bareBucket.push(ch);
            else _byBareName.set(bareKey, [ch]);
        }
    }

    /**
     * Rebuild the liveId → programmes window index from `windowProgrammes`.
     * Called exclusively from `applyWindowPayload` after the window arrays
     * are populated; deliberately separate from `_rebuildIndex` so the two
     * index families cannot accidentally clobber each other.
     */
    function _rebuildWindowIndex(): void {
        _byLiveId = new Map();
        for (const p of windowProgrammes.value) {
            if (!p.liveId) continue;
            const bucket = _byLiveId.get(p.liveId);
            if (bucket) bucket.push(p);
            else _byLiveId.set(p.liveId, [p]);
        }
    }

    // -----------------------------------------------------------------------
    // Guide-scoped accumulating window helpers (Phase 1 §A)
    // -----------------------------------------------------------------------

    /** Drain all Guide-scoped accumulation (call on unmount + time-nav reset). */
    function _clearGuideState(): void {
        // Invalidate any response still in flight for the previous window.
        // The serialized flush loop may continue with ids queued after this
        // reset, but it must never merge the stale request it was awaiting.
        _guideSeq++;
        guideChannels.value = [];
        guideProgrammes.value = [];
        _guideByLiveId = new Map();
        _guideLoadedLiveIds.clear();
        _guidePendingIds = new Set();
        // Batch 1 perf fix — drain the persistent merge-dedupe sets alongside
        // everything else this function drains. Without this, a channel /
        // programme that was "seen" before the clear would stay marked seen
        // forever, silently swallowing a legitimately new post-clear row with
        // the same key (see the module-level singletons' doc comment).
        _guideSeenChannelKeys = new Set();
        _guideSeenProgrammeKeys = new Set();
        // Guide-row-status Batch 2 — a bulk "loaded" → "not loaded" flip also
        // drops any failed-fetch marker; a stale one would render the
        // failed treatment for a channel this reset just made "never
        // fetched" again.
        _guideFailedLiveIds = new Set();
        // Do not clear `_guideFlushPromise` while its request is still in
        // flight. New-window ids must join that same serialized drain; letting
        // a second loop start would reintroduce the response-order race this
        // queue exists to prevent. The active loop's `finally` clears it once
        // both the stale request and any newly queued ids have drained.
        // Bulk "loaded" → "not loaded" flip — bump the reactive touch so
        // `isGuideChannelLoaded` consumers re-run (see its doc comment).
        _guideLoadedVersion.value++;
    }

    /**
     * Canonical identity key for a guide programme row — `${liveId}:${startMs}:${stopMs}`
     * (post-4.10 hotfix #4). Shared by `_mergeGuidePayload` (the ONLY place
     * that ADDS to `_guideSeenProgrammeKeys`) and `refetchWindowChannel` (the
     * only place that needs to REMOVE from it) so the two never drift apart —
     * a hand-rolled second copy of this template string is exactly how that
     * drift would happen silently.
     */
    function _guideProgrammeKey(liveId: string | undefined, startMs: number, stopMs: number): string {
        return `${liveId}:${startMs}:${stopMs}`;
    }

    /**
     * Merge a `POST /api/proxy/epg/window` response INTO the guide accumulation.
     * Dedupes channels by `liveId` and programmes by the full identity tuple
     * `${liveId}:${startMs}:${stopMs}` (post-4.10 hotfix #4: dedup at every
     * merge boundary). Updates `_guideByLiveId` incrementally — does NOT
     * rebuild from scratch so the append-only contract is maintained.
     *
     * Batch 1 perf fix — the dedupe keys (`_guideSeenChannelKeys` /
     * `_guideSeenProgrammeKeys`) are PERSISTENT module-level Sets, added to
     * incrementally as rows are accepted. This used to rebuild both sets from
     * scratch via `.map()` over the full `guideChannels.value` /
     * `guideProgrammes.value` arrays on every call — O(total accumulated) on
     * every scroll-triggered batch, on the main thread, at exactly the moment
     * new rows need to paint. The persistent sets are drained by
     * `_clearGuideState()` (and, in tests, `_resetGuideWindowForTests()`) —
     * and, for a SINGLE channel's keys, by `refetchWindowChannel` (R1 review
     * Blocker 1) — so neither a time-nav reset nor a per-channel purge can
     * leave a stale "seen" entry that would silently drop a legitimately new
     * row afterward.
     */
    function _mergeGuidePayload(payload: EpgWindowResponse): void {
        const addedChannels: EpgChannelWithSource[] = [];
        const addedProgrammes: ProgrammeWithSource[] = [];

        for (const ch of payload.channels ?? []) {
            // Key is `liveId` = `${liveSourceId}:${liveItemId}`.
            const key = ch.liveId;
            if (_guideSeenChannelKeys.has(key)) continue;
            _guideSeenChannelKeys.add(key);
            addedChannels.push(markRaw({
                id: ch.itemId,
                name: ch.name,
                sourceId: ch.sourceId,
                icon: ch.icon,
                language: ch.language,
                liveItemId: ch.itemId,
                liveSourceId: ch.sourceId,
            }));
        }

        for (const p of payload.programmes ?? []) {
            const startMs = parseTimestamp(p.start);
            const stopMs = parseTimestamp(p.stop);
            if (!Number.isFinite(startMs) || !Number.isFinite(stopMs)) continue;
            const key = _guideProgrammeKey(p.liveId, startMs, stopMs);
            if (_guideSeenProgrammeKeys.has(key)) continue;
            _guideSeenProgrammeKeys.add(key);
            const row = markRaw({
                ...p,
                sourceId: p.sourceId,
                channelId: p.channelId,
                liveId: p.liveId,
                epgSourceId: p.epgSourceId,
                epgChannelId: p.epgChannelId,
                startMs,
                stopMs,
            });
            addedProgrammes.push(row);
            // Update `_guideByLiveId` incrementally.
            if (p.liveId) {
                const bucket = _guideByLiveId.get(p.liveId);
                if (bucket) bucket.push(row);
                else _guideByLiveId.set(p.liveId, [row]);
            }
        }

        if (addedChannels.length > 0 || addedProgrammes.length > 0) {
            // Replace refs (shallowRef — new array reference triggers reactivity).
            guideChannels.value = [...guideChannels.value, ...addedChannels];
            guideProgrammes.value = [...guideProgrammes.value, ...addedProgrammes];
        }
    }

    /**
     * Set the time window the Guide currently needs covered.
     *
     * Batch 1 perf fix — CONTAINMENT-AWARE, not exact-match. `opts` is the
     * caller's REQUIRED window (`Guide.vue` sends viewport ± its own nav-step
     * margin — narrower than what actually gets fetched). If `opts` is fully
     * contained in `_guideFetchedSpan` (the span the last real fetch
     * covered), the accumulated data already answers it — this is a no-op:
     * no clear, no refetch. Only a window that reaches OUTSIDE the fetched
     * span triggers `_clearGuideState()` + a fresh fetch.
     *
     * When a fetch IS needed, the actual fetched/tracked span is `opts`
     * padded by `GUIDE_WINDOW_FETCH_PAD_MS` on each side — NOT `opts` itself.
     * This is load-bearing, not decoration: two windows of the SAME width
     * can only be subsets of each other if they're identical (shift the
     * interval at all and neither contains the other), so if the caller's
     * requested width never changes, an un-padded fetched span could only
     * ever satisfy a containment check for a zero-length nav move. Padding
     * by at least the Guide's nav step (`GUIDE_WINDOW_NAV_STEP_MS`) is what
     * guarantees a single Prev/Next press after a fetch lands inside the
     * PREVIOUS fetched span and skips the clear+refetch entirely — the
     * "‹ −2h / +2h ›" trap this batch's plan flagged: narrowing the fetch
     * without this would trade the vertical-scroll lag it fixes for a new
     * horizontal time-nav lag on literally the next button press.
     *
     * Phase 1 §A — D3 scope contract: does NOT touch Live's `window*` state.
     */
    async function setGuideWindow(opts: { startMs: number; endMs: number }): Promise<void> {
        const contained = _guideFetchedSpan !== null
            && opts.startMs >= _guideFetchedSpan.startMs
            && opts.endMs <= _guideFetchedSpan.endMs;
        if (contained) return;

        const padded = {
            startMs: opts.startMs - GUIDE_WINDOW_FETCH_PAD_MS,
            endMs: opts.endMs + GUIDE_WINDOW_FETCH_PAD_MS,
        };

        // R1 review Blocker 3 — an overlapping (scroll-driven) change EXTENDS
        // the accumulation instead of wiping it. `setGuideWindow` used to
        // `_clearGuideState()` on ANY non-contained change, which meant
        // horizontal scrubbing (Blocker 2's own fetch trigger) wiped the
        // ENTIRE guide accumulation — every channel, including rows that had
        // scrolled off vertically — on every direction change, and
        // `_clearGuideState()` bumping `_guideSeq` cancelled whatever was
        // still in flight on top of that. A union is safe here specifically
        // because it never leaves a GAP: the fetched span stays one
        // contiguous interval, and the delta fetch below covers exactly the
        // part that widened.
        const oldSpan = _guideFetchedSpan;
        if (oldSpan !== null && _spansOverlap(oldSpan, padded)) {
            const union = {
                startMs: Math.min(oldSpan.startMs, padded.startMs),
                endMs: Math.max(oldSpan.endMs, padded.endMs),
            };
            if (union.endMs - union.startMs <= GUIDE_WINDOW_MAX_SPAN_MS) {
                await _extendGuideWindow(oldSpan, union);
                return;
            }
            // Union would exceed the bound — fall through to a hard reset
            // anchored on the NEW request, same as the disjoint-jump case.
        }

        // Disjoint jump (day-nav to a span nothing already fetched touches)
        // or the union-bound was exceeded — a union would either span a
        // useless unfetched gap or grow without limit, so a clear is correct.
        _clearGuideState();
        _guideWindow = padded;
        _guideFetchedSpan = padded;
        log.info('epg.guide_window_set', { startMs: padded.startMs, endMs: padded.endMs });
    }

    /**
     * R1 review Blocker 3 — extend the guide's fetched span to `union`
     * (which callers have already verified overlaps `oldSpan` and stays
     * within `GUIDE_WINDOW_MAX_SPAN_MS`) WITHOUT clearing the accumulation.
     *
     * `_guideWindow` vs `_guideFetchedSpan` — two different questions,
     * deliberately allowed to diverge (R1 review Blocker 4):
     *   - `_guideWindow` is the window a NEWLY-visible (never-loaded) liveId's
     *     full fetch uses. Safe to set to `union` immediately, synchronously:
     *     a brand-new id has nothing accumulated to protect, so fetching its
     *     whole CURRENT logical window is correct no matter how the delta
     *     fetches below turn out for the ALREADY-loaded ids.
     *   - `_guideFetchedSpan` is what `setGuideWindow`'s containment check
     *     trusts as "already fetched, safe to no-op a request against." It
     *     MUST only ever claim territory a fetch actually SUCCEEDED for.
     *     Blocker 4's original version of this function set it to `union`
     *     up front, before any fetch ran, and never rolled it back on
     *     failure — a failed delta then permanently recorded coverage the
     *     store had no data for, and every later `setGuideWindow` over that
     *     region would silently no-op forever. This version starts it at
     *     `oldSpan` and widens it edge-by-edge, ONLY after a delta's
     *     fetch(es) for every currently-loaded id succeed.
     *
     * Only ids ALREADY in `_guideLoadedLiveIds` need this fetch — a
     * currently-loaded channel's accumulated programmes stop at the OLD
     * span's edge, so its lane would render blank past that edge until this
     * fills it in. Fetching only the delta (not the full union) per
     * already-loaded id is the entire point — it's what makes this
     * affordable within the `GUIDE_WINDOW_MAX_SPAN_MS` bound.
     *
     * `_mergeGuidePayload`'s dedupe makes an overlapping response harmless,
     * so the delta does not need to be pixel-exact — it only needs to not
     * silently skip real territory, which the min/max edges below guarantee.
     */
    async function _extendGuideWindow(
        oldSpan: { startMs: number; endMs: number },
        union: { startMs: number; endMs: number },
    ): Promise<void> {
        _guideWindow = union;
        log.info('epg.guide_window_extended', { startMs: union.startMs, endMs: union.endMs });

        const deltas: Array<{ startMs: number; endMs: number }> = [];
        if (union.startMs < oldSpan.startMs) deltas.push({ startMs: union.startMs, endMs: oldSpan.startMs });
        if (union.endMs > oldSpan.endMs) deltas.push({ startMs: oldSpan.endMs, endMs: union.endMs });
        if (deltas.length === 0) return; // defensive — the overlap+non-contained precondition should always produce at least one

        const loadedIds = [..._guideLoadedLiveIds];
        if (loadedIds.length === 0) {
            // Nothing accumulated to backfill — no channel is owed data for
            // the new territory, so it is trivially fully covered.
            _guideFetchedSpan = union;
            return;
        }

        // Sequential, NOT Promise.all — `_guideSeq` is a single global
        // "most recent request" marker (the same one `ensureGuideChannels`'s
        // serialized flush loop relies on), not a per-request token. Firing
        // both deltas concurrently would have each `++_guideSeq` invalidate
        // the OTHER while nothing was actually cleared, so a two-sided widen
        // (union grows on both edges at once) would silently drop one delta's
        // response. At most 2 deltas ever exist here, so the small
        // sequential-vs-parallel latency difference is not worth the risk.
        //
        // `aborted` — R1 review SHOULD FIX: `ensureGuideChannels`'s sibling
        // loop guards EVERY iteration with `if (... || !_guideWindow)
        // continue;`; this one had nothing, so a clear landing BETWEEN two
        // chunks (day-nav jump or the 5-min background refresh) only
        // invalidated the chunk that was actually in flight when it
        // resolved — the NEXT chunk still fired, captured its OWN fresh
        // `++_guideSeq`, and that fresh capture trivially matched
        // `_guideSeq` on its own resolution (nothing bumped it again after),
        // so it merged old-region programmes straight into the
        // freshly-cleared accumulation and seeded the just-emptied seen-keys
        // with them. `!_guideWindow` alone would not have caught this — a
        // day-nav jump RE-SETS `_guideWindow` to a new non-null window
        // rather than nulling it — so `aborted` also latches on the
        // staleness signal itself: once ANY chunk detects it has been
        // superseded (by a clear OR by an unrelated newer fetch batch), no
        // further chunk or delta in THIS call fires. An ordinary per-chunk
        // failure (network error, no supersession involved) does NOT set
        // `aborted` — it only gives up on that one delta's edge, exactly as
        // before, so a transient blip on one chunk doesn't cancel a
        // still-valid attempt at the other delta.
        let aborted = false;
        for (const delta of deltas) {
            if (aborted) break;
            // A delta's edge is claimed in `_guideFetchedSpan` only if EVERY
            // chunk below succeeds. Being conservative (all-or-nothing per
            // edge, not per chunk) costs a redundant re-fetch of whichever
            // chunks DID succeed on a later retry — harmless, since
            // `_mergeGuidePayload` dedupes — versus the alternative of
            // partially claiming an edge some loaded channels have no data
            // for, which is exactly the false-coverage bug this fix closes.
            let deltaOk = true;
            // Bound + chunk to GUIDE_FETCH_CAP (R1 review Blocker 4, part 2).
            // `loadedIds` accumulates across the WHOLE session (every
            // channel ever scrolled past) — unlike every OTHER caller of
            // `ensureGuideChannels`, which pre-slices to this same cap
            // before calling in, so an unbounded request here can exceed the
            // backend's hard `MAX_LIVE_IDS` (2000) and 413 — the realistic
            // path to the failure this whole function now guards against.
            for (let i = 0; i < loadedIds.length; i += GUIDE_FETCH_CAP) {
                if (!_guideWindow) {
                    // Reset (e.g. Guide.vue unmount → resetGuideWindow) since
                    // this call started — stop issuing requests nothing will
                    // observe.
                    aborted = true;
                    deltaOk = false;
                    break;
                }
                const chunk = loadedIds.slice(i, i + GUIDE_FETCH_CAP);
                const mySeq = ++_guideSeq;
                try {
                    const payload = await api.proxy.epg.window({
                        liveIds: chunk,
                        startMs: delta.startMs,
                        endMs: delta.endMs,
                    });
                    if (mySeq !== _guideSeq) {
                        // Superseded by a clear or another fetch batch while
                        // in flight — stop entirely (see `aborted`'s doc
                        // comment above); the span this delta would have
                        // claimed may not even describe the current window
                        // any more.
                        aborted = true;
                        deltaOk = false;
                        break;
                    }
                    _mergeGuidePayload(payload);
                    log.info('epg.guide_window_extend_loaded', {
                        liveIds: chunk.length,
                        startMs: delta.startMs,
                        endMs: delta.endMs,
                        cacheHit: payload.cacheHit,
                    });
                } catch (err) {
                    deltaOk = false;
                    log.warn('epg.guide_window_extend_failed', {
                        liveIds: chunk.length,
                        error: (err as Error).message,
                    });
                }
            }
            if (deltaOk && _guideFetchedSpan !== null) {
                _guideFetchedSpan = {
                    startMs: Math.min(_guideFetchedSpan.startMs, delta.startMs),
                    endMs: Math.max(_guideFetchedSpan.endMs, delta.endMs),
                };
            }
        }
    }

    /**
     * Reset all Guide-scoped state (call on `Guide.vue` unmount so a
     * subsequent mount starts fresh). Also clears the active window so
     * `setGuideWindow` will treat the next call as a fresh init.
     *
     * Phase 1 §A — D3 scope contract: does NOT touch Live's `window*` state.
     */
    function resetGuideWindow(): void {
        _clearGuideState();
        _guideWindow = null;
        _guideFetchedSpan = null;
        // The horizon probe is deliberately NOT part of `_clearGuideState`
        // (it must survive every ordinary time-nav window change so the day
        // strip doesn't flicker/rebuild on every ±2h nudge) but a page
        // unmount should drop it like everything else Guide-scoped.
        guideHorizonProgrammes.value = [];
        log.info('epg.guide_window_reset');
    }

    /**
     * Ensure the Guide has EPG data for the given liveIds. Only fetches ids
     * that are NOT already in `_guideLoadedLiveIds` for the active window.
     * Concurrent calls are COALESCED: ids are collected into `_guidePendingIds`
     * and flushed on the next microtask — rapid scroll events that call
     * `ensureGuideChannels` multiple times before the tick produce one batched
     * request rather than many parallel ones.
     *
     * If no `_guideWindow` is set, this is a no-op (caller must call
     * `setGuideWindow` first).
     *
     * Phase 1 §A — D3: does NOT write to Live's `window*` / `windowChannels` /
     * `windowProgrammes`. Uses the same `api.proxy.epg.window` endpoint.
     */
    async function ensureGuideChannels(liveIds: string[]): Promise<void> {
        if (!_guideWindow) return;
        // Client-side validate: only send non-empty string ids (§C).
        const valid = liveIds.filter((id) => typeof id === 'string' && id.length > 0);
        if (valid.length === 0) return;

        // Coalesce into pending set — skip ids already loaded.
        let added = false;
        for (const id of valid) {
            if (!_guideLoadedLiveIds.has(id)) {
                _guidePendingIds.add(id);
                added = true;
            }
        }
        if (!added) return;

        // Keep ONE promise alive for the entire queue drain. Calls arriving
        // while a request is in flight append to `_guidePendingIds` and await
        // the same loop; the next batch starts only after the prior response
        // has merged. This prevents a later visible-range request advancing
        // `_guideSeq` and discarding a focused-channel response whose id was
        // already optimistically marked loaded.
        if (_guideFlushPromise === null) {
            _guideFlushPromise = Promise.resolve().then(async () => {
                while (_guidePendingIds.size > 0) {
                    const toFetch = [..._guidePendingIds].filter((id) => !_guideLoadedLiveIds.has(id));
                    _guidePendingIds = new Set();
                    if (toFetch.length === 0 || !_guideWindow) continue;

                    // Mark as loaded BEFORE the fetch so concurrent calls that
                    // arrive while it is in flight don't re-queue these ids.
                    // Guide-row-status Batch 2 — a (re)attempt also clears any
                    // stale failed-marker for these ids up front: a retry
                    // that is currently in flight must render as "loading",
                    // not "failed", for the duration of this attempt.
                    for (const id of toFetch) {
                        _guideLoadedLiveIds.add(id);
                        _guideFailedLiveIds.delete(id);
                    }

                    const mySeq = ++_guideSeq;
                    const { startMs, endMs } = _guideWindow;
                    try {
                        const payload = await api.proxy.epg.window({ liveIds: toFetch, startMs, endMs });
                        // A Guide window reset invalidates this response — but
                        // `_clearGuideState()` is no longer the ONLY other
                        // `_guideSeq` writer (R1 review, cross-function
                        // blocker): `_extendGuideWindow` also bumps it, once
                        // per chunk per delta, WITHOUT touching
                        // `_guideLoadedLiveIds`. Before that existed, "stale"
                        // always meant "a clear ran", and the clear had
                        // already un-marked these ids as loaded in the same
                        // breath — so a bare `continue` was safe by
                        // construction. It no longer is: an unrelated widen
                        // can bump `_guideSeq` while THIS fetch is in flight,
                        // and without the rollback below these ids would stay
                        // marked loaded forever with no data ever merged for
                        // them — permanently blank, no retry possible, since
                        // `ensureGuideChannels`'s own loaded-check is what
                        // would normally trigger one. Roll back exactly like
                        // the `catch` block already does; it's a harmless
                        // no-op on ids a clear already removed.
                        if (mySeq !== _guideSeq) {
                            for (const id of toFetch) _guideLoadedLiveIds.delete(id);
                            continue;
                        }
                        _mergeGuidePayload(payload);
                        _guideLoadedVersion.value++;
                        log.info('epg.guide_channels_loaded', {
                            fetched: toFetch.length,
                            channels: guideChannels.value.length,
                            programmes: guideProgrammes.value.length,
                            cacheHit: payload.cacheHit,
                        });
                    } catch (err) {
                        // Roll back the loaded-set so the caller can retry.
                        for (const id of toFetch) _guideLoadedLiveIds.delete(id);
                        // Guide-row-status Batch 2 — only mark a GENUINE
                        // failure (this request is still the authoritative
                        // one — `mySeq === _guideSeq`). A request that was
                        // superseded by a clear or a newer fetch batch
                        // rolls back exactly like before but must NOT render
                        // as "failed" — that would tell the user retry is
                        // needed for a request nothing actually asked for
                        // any more; the next visibility trigger already
                        // re-fetches it as an ordinary "not loaded yet" id.
                        if (mySeq === _guideSeq) {
                            for (const id of toFetch) _guideFailedLiveIds.add(id);
                        }
                        _guideLoadedVersion.value++;
                        if (mySeq === _guideSeq) {
                            log.warn('epg.guide_channels_failed', {
                                count: toFetch.length,
                                error: (err as Error).message,
                            });
                        }
                    }
                }
            }).finally(() => {
                _guideFlushPromise = null;
            });
        }
        await _guideFlushPromise;
    }

    /**
     * Returns `true` if `liveId` has at least one programme in the Guide's
     * current-window accumulation. NOT the same question as "has this id
     * been fetched" — a channel the backend has no data for is fetched
     * (`isGuideChannelLoaded` below) but never returns `true` here.
     *
     * Phase 1 §A reactive: reads `guideProgrammes.value` so computed consumers
     * re-run when the guide accumulation updates.
     */
    function hasGuideEpgForLiveId(liveId: string | null | undefined): boolean {
        void guideProgrammes.value; // reactive touch — same pattern as hasEpgForLiveId
        return !!liveId && (_guideByLiveId.get(liveId)?.length ?? 0) > 0;
    }

    /**
     * Returns `true` once `liveId` has been through an `ensureGuideChannels`
     * fetch for the CURRENT window (success or not — a rolled-back failed
     * fetch is un-loaded again, so a later call retries it). Distinguishes
     * "no data because we haven't checked yet" from "confirmed no data" —
     * the D6/U1 fix (`Guide.vue`'s `visibleGroupedChannels`) needs exactly
     * this distinction: an unchecked channel must stay visible under
     * "Hide empty" so it CAN be checked (filtering it out first would mean
     * it can never become visible-to-the-grid, so `ensureGuideChannels`
     * would never be asked about it, so its state could never resolve —
     * `hideNoData` persists across sessions, so this is reachable on a
     * completely fresh mount, not just a mid-session toggle).
     */
    function isGuideChannelLoaded(liveId: string | null | undefined): boolean {
        void _guideLoadedVersion.value; // reactive touch — see the ref's doc comment
        return !!liveId && _guideLoadedLiveIds.has(liveId);
    }

    /**
     * Guide-row-status Batch 2 — `true` when `liveId`'s MOST RECENT
     * `ensureGuideChannels` attempt errored and rolled back (network/API
     * failure, not a stale-response supersession — see that catch block's
     * doc comment). Mutually exclusive with `isGuideChannelLoaded`: a
     * failed id is removed from the loaded set by the same rollback that
     * adds it here, and the very next fetch attempt for it clears this flag
     * again BEFORE the attempt settles (so a retry-in-flight reads as
     * "loading", not "still failed"). Reactive via the same
     * `_guideLoadedVersion` touch `isGuideChannelLoaded` uses — both flip at
     * the same settlement points, by construction (one `catch` block writes
     * both).
     */
    function isGuideChannelFailed(liveId: string | null | undefined): boolean {
        void _guideLoadedVersion.value; // reactive touch — see the ref's doc comment
        return !!liveId && _guideFailedLiveIds.has(liveId);
    }

    /**
     * Return programmes for `liveId` from the Guide's accumulation.
     * Reactive via `guideProgrammes` touch.
     */
    function guideProgrammesFor(liveId: string | null | undefined): ProgrammeWithSource[] {
        void guideProgrammes.value; // reactive touch
        if (!liveId) return [];
        return _guideByLiveId.get(liveId) ?? [];
    }

    /**
     * Fetch a one-shot, wider EPG window for `liveIds` (a sample — typically
     * whatever the Guide currently has visible) purely to learn which
     * calendar days have programme data, so the day-nav strip never has to
     * guess or hardcode a day count. Replaces `guideHorizonProgrammes`
     * wholesale; failures are logged and swallowed — the day strip degrades
     * to "Today only" rather than surfacing an error for a non-critical
     * affordance.
     */
    /**
     * X3 day-nav source — the UNION of the horizon probe's programmes and
     * everything the lazy per-viewport loads have accumulated. Derived in
     * ONE place because two consumers (the day strip in EpgFilters and the
     * PageUp/PageDown clamp in Guide.vue) must agree on which days exist;
     * the union also means the strip GROWS as EPG-bearing channels load,
     * even when the horizon probe itself sampled channels with no data.
     */
    function guideDayNavStartTimes(): number[] {
        const out: number[] = [];
        for (const p of guideHorizonProgrammes.value) out.push(p.startMs);
        for (const p of guideProgrammes.value) out.push(p.startMs);
        return out;
    }

    /**
     * Parse a window payload's programme rows into `ProgrammeWithSource`.
     * Shared by the two REPLACE-semantics point-in-time loaders below
     * (`loadGuideHorizon`, `loadNotifierWindow`) so the timestamp guard and
     * the `markRaw` shape exist once. `applyWindowPayload` keeps its own
     * copy of this loop because it additionally dedupes against already-held
     * rows at the merge boundary — a different contract.
     */
    function _parseWindowProgrammes(payload: EpgWindowResponse): ProgrammeWithSource[] {
        const rows: ProgrammeWithSource[] = [];
        for (const p of payload.programmes ?? []) {
            const rStart = parseTimestamp(p.start);
            const rStop = parseTimestamp(p.stop);
            if (!Number.isFinite(rStart) || !Number.isFinite(rStop)) continue;
            rows.push(markRaw({
                ...p,
                sourceId: p.sourceId,
                channelId: p.channelId,
                liveId: p.liveId,
                epgSourceId: p.epgSourceId,
                epgChannelId: p.epgChannelId,
                startMs: rStart,
                stopMs: rStop,
            }));
        }
        return rows;
    }

    async function loadGuideHorizon(liveIds: string[], startMs: number, endMs: number): Promise<void> {
        const unique = [...new Set(liveIds)].filter((id) => typeof id === 'string' && id.length > 0);
        if (unique.length === 0) return;
        try {
            const payload = await api.proxy.epg.window({ liveIds: unique, startMs, endMs });
            const rows = _parseWindowProgrammes(payload);
            guideHorizonProgrammes.value = rows;
            log.info('epg.guide_horizon_loaded', { liveIds: unique.length, programmes: rows.length });
        } catch (err) {
            log.warn('epg.guide_horizon_failed', { error: (err as Error).message });
        }
    }

    /**
     * EPG start notifications (Spec 10) — load the notifier's own window.
     *
     * REPLACES `notifierProgrammes` (point-in-time plan input, same contract
     * as `loadGuideHorizon`). Writes NOTHING else: not `channels` /
     * `programmes`, not the `window*` refs, not `_lastWindow`, so it cannot
     * disturb Live's poller or the Guide's accumulation.
     *
     * `liveIds` is CHUNKED. The favourites list is user-controlled and the
     * endpoint answers HTTP 413 above `MAX_LIVE_IDS` (2000,
     * `server/services/epgWindowQuery.js`) — an unchunked request would take
     * the whole feature down for exactly the users who favourite the most
     * channels, and silently (the notifier has no visible surface to fail
     * into). Chunks are fetched sequentially and concatenated; a failing
     * chunk is logged and skipped rather than discarding the ones that
     * succeeded.
     */
    async function loadNotifierWindow(liveIds: string[], startMs: number, endMs: number): Promise<void> {
        const unique = [...new Set(liveIds)].filter((id) => typeof id === 'string' && id.length > 0);
        if (unique.length === 0) {
            notifierProgrammes.value = [];
            notifierChannelNames.value = new Map();
            return;
        }
        // Reuses the Guide's chunk size rather than inventing a second number:
        // it is the one measured per-request id count this client already
        // trusts, and it sits comfortably under the server's own hard cap
        // (`MAX_LIVE_IDS = 2000`, `server/services/epgWindowQuery.js`). It is
        // NOT derived from that cap — if the Guide ever retunes its constant
        // for payload-size reasons, the notifier follows, which is harmless
        // in both directions as long as it stays below 2000.
        const perRequestCap = GUIDE_FETCH_CAP;
        const rows: ProgrammeWithSource[] = [];
        const names = new Map<string, string>();
        let failedChunks = 0;
        for (let i = 0; i < unique.length; i += perRequestCap) {
            const chunk = unique.slice(i, i + perRequestCap);
            try {
                const payload = await api.proxy.epg.window({ liveIds: chunk, startMs, endMs });
                rows.push(..._parseWindowProgrammes(payload));
                // The channel NAME is what makes a notification identifiable —
                // "News · starts in 1 minute" is useless across eight
                // favourites. It rides in `payload.channels`, never on a
                // programme row.
                for (const ch of payload.channels ?? []) {
                    if (ch.liveId && ch.name) names.set(ch.liveId, ch.name);
                }
            } catch (err) {
                failedChunks++;
                log.warn('epg.notifier_window_chunk_failed', {
                    liveIds: chunk.length,
                    error: (err as Error).message,
                });
            }
        }
        notifierProgrammes.value = rows;
        notifierChannelNames.value = names;
        log.info('epg.notifier_window_loaded', {
            liveIds: unique.length,
            chunks: Math.ceil(unique.length / perRequestCap),
            failedChunks,
            programmes: rows.length,
            namedChannels: names.size,
        });
    }

    /**
     * MERGE a `POST /api/proxy/epg/window` response into the window-scoped
     * state (`windowChannels`, `windowProgrammes`, `_byLiveId`).
     *
     * Merge — not replace. The window namespace is shared by every Live-page
     * consumer that keys by liveId (now/next panel, player tune-in card,
     * `<ChannelTile>` inline EPG): the current channel's `loadWindow` on each
     * hop and the sidebar's `ensureWindowChannels` for visible rows accumulate
     * into the same index without clobbering each other. Dedup at the merge
     * boundary by the full identity tuple — channels by `liveId`, programmes
     * by `(liveId, startMs, stopMs, title)` (post-4.10 rule #4).
     *
     * CONTRACT: does NOT assign to `channels.value` / `programmes.value` and
     * does NOT rebuild the legacy `_byTvgId` / `_byPrefixedName` / `_byBareName`
     * indexes. Those arrays are the exclusive property of `load()`. The two
     * state families coexist without clobbering.
     */
    function applyWindowPayload(payload: EpgWindowResponse): void {
        const seenChannels = new Set<string>(
            windowChannels.value.map((ch) => `${ch.liveSourceId}:${ch.liveItemId}`),
        );
        const seenProgrammes = new Set<string>(
            windowProgrammes.value.map((p) => `${p.liveId}:${p.startMs}:${p.stopMs}:${p.title}`),
        );
        const addChannels: EpgChannelWithSource[] = [];
        const addProgrammes: ProgrammeWithSource[] = [];
        for (const ch of payload.channels ?? []) {
            if (seenChannels.has(ch.liveId)) continue;
            seenChannels.add(ch.liveId);
            addChannels.push(markRaw({
                id: ch.itemId,
                name: ch.name,
                sourceId: ch.sourceId,
                icon: ch.icon,
                language: ch.language,
                liveItemId: ch.itemId,
                liveSourceId: ch.sourceId,
            }));
        }
        for (const p of payload.programmes ?? []) {
            const startMs = parseTimestamp(p.start);
            const stopMs = parseTimestamp(p.stop);
            if (!Number.isFinite(startMs) || !Number.isFinite(stopMs)) continue;
            const key = `${p.liveId}:${startMs}:${stopMs}:${p.title}`;
            if (seenProgrammes.has(key)) continue;
            seenProgrammes.add(key);
            addProgrammes.push(markRaw({
                ...p,
                sourceId: p.sourceId,
                channelId: p.channelId,
                liveId: p.liveId,
                epgSourceId: p.epgSourceId,
                epgChannelId: p.epgChannelId,
                startMs,
                stopMs,
            }));
        }
        // Write to window-scoped state only. The shared `channels` / `programmes`
        // refs are untouched — Live/Watch / legacy four-tier resolver keep their data.
        if (addChannels.length) windowChannels.value = [...windowChannels.value, ...addChannels];
        if (addProgrammes.length) {
            windowProgrammes.value = [...windowProgrammes.value, ...addProgrammes];
            _rebuildWindowIndex();
        }
        lastLoadedAt.value = Date.now();
    }

    async function loadWindow(opts: EpgWindowLoad): Promise<void> {
        const liveIds = [...new Set(opts.liveIds || [])];
        const hash = _windowHash({ ...opts, liveIds });
        const lastAt = _filterStaleAt.get(hash) || 0;
        if (!opts.force && lastAt > 0 && Date.now() - lastAt < REFRESH_INTERVAL_MS) {
            _lastWindow = { ...opts, liveIds };
            return;
        }
        const mySeq = ++_loadSeq.value;
        isLoading.value = true;
        try {
            if (liveIds.length === 0) {
                if (mySeq === _loadSeq.value) {
                    windowChannels.value = [];
                    windowProgrammes.value = [];
                    _invalidateWindowIndex();
                    _windowLoadedAt.clear();
                    _windowPendingIds = new Set();
                    lastLoadedAt.value = Date.now();
                }
                _lastWindow = { ...opts, liveIds };
                _filterStaleAt.set(hash, Date.now());
                return;
            }
            const payload = await api.proxy.epg.window({
                liveIds,
                startMs: opts.startMs,
                endMs: opts.endMs,
            });
            if (mySeq !== _loadSeq.value) return;
            applyWindowPayload(payload);
            // Stamp these ids as freshly loaded so `ensureWindowChannels`
            // (the sidebar's visible-rows path) doesn't refetch them.
            const stamp = Date.now();
            for (const id of liveIds) _windowLoadedAt.set(id, stamp);
            _lastWindow = { ...opts, liveIds };
            _filterStaleAt.set(hash, Date.now());
            log.info('epg.window_loaded', {
                liveIds: liveIds.length,
                channels: windowChannels.value.length,
                programmes: windowProgrammes.value.length,
                cacheHit: payload.cacheHit,
            });
        } catch (err) {
            log.warn('epg.window_load_failed', { error: (err as Error).message });
        } finally {
            if (mySeq === _loadSeq.value) isLoading.value = false;
        }
    }

    // -----------------------------------------------------------------------
    // Window-scoped incremental ensure — the Live sidebar's inline-EPG path.
    // -----------------------------------------------------------------------

    /** liveId → Date.now() when its window EPG last loaded (staleness gate). */
    const _windowLoadedAt = new Map<string, number>();
    let _windowPendingIds = new Set<string>();
    let _windowFlushPromise: Promise<void> | null = null;
    let _windowEnsureSeq = 0;

    /**
     * Ensure the Live window accumulation has EPG for the given liveIds —
     * the `<ChannelSidebar>` calls this with the channel rows currently
     * visible in its VirtualList (group expansion, scrolling, search all
     * funnel through visibility). Mirrors `ensureGuideChannels`' coalescing
     * (ids batched on a microtask so one scroll tick = one POST) but merges
     * into the WINDOW namespace `applyWindowPayload` owns, so the existing
     * liveId-keyed consumers (`<ChannelTile>` inline line, now/next panel,
     * tune-in card) populate with zero extra wiring.
     *
     * Unlike the Guide there is no explicit window/reset lifecycle: each
     * flush anchors its fetch range at "now" (-2h … +8h, same shape as
     * Live.vue's current-channel load) and ids go stale after
     * REFRESH_INTERVAL_MS so a later visibility trigger refetches them.
     */
    /**
     * Drop the freshness stamps for liveIds so the next
     * `ensureWindowChannels` call refetches them. Used after an EPG-mapping
     * override saves (post-4.10 rule #7 — cross-store mutations ship an
     * explicit refetch signal; without this the freshness window swallows
     * the refetch and the new mapping doesn't render until it expires).
     */
    function invalidateWindowChannels(liveIds: string[]): void {
        for (const id of liveIds) _windowLoadedAt.delete(id);
    }

    async function ensureWindowChannels(liveIds: string[]): Promise<void> {
        const nowMs = Date.now();
        let added = false;
        for (const id of liveIds) {
            if (typeof id !== 'string' || id.length === 0) continue;
            const at = _windowLoadedAt.get(id) ?? 0;
            if (nowMs - at < REFRESH_INTERVAL_MS) continue;
            if (!_windowPendingIds.has(id)) {
                _windowPendingIds.add(id);
                added = true;
            }
        }
        if (!added) return _windowFlushPromise ?? undefined;
        if (_windowFlushPromise !== null) return _windowFlushPromise;

        _windowFlushPromise = Promise.resolve().then(async () => {
            _windowFlushPromise = null;
            const flushAt = Date.now();
            const toFetch = [..._windowPendingIds].filter(
                (id) => flushAt - (_windowLoadedAt.get(id) ?? 0) >= REFRESH_INTERVAL_MS,
            );
            _windowPendingIds = new Set();
            if (toFetch.length === 0) return;

            // Stamp BEFORE the fetch so concurrent calls don't re-queue the
            // same ids while this batch is in flight; rolled back on error.
            for (const id of toFetch) _windowLoadedAt.set(id, flushAt);

            const mySeq = ++_windowEnsureSeq;
            try {
                const payload = await api.proxy.epg.window({
                    liveIds: toFetch,
                    startMs: flushAt - 2 * 3_600_000,
                    endMs: flushAt + 8 * 3_600_000,
                });
                if (mySeq !== _windowEnsureSeq) return;
                applyWindowPayload(payload);
                log.info('epg.window_channels_loaded', {
                    fetched: toFetch.length,
                    programmes: windowProgrammes.value.length,
                    cacheHit: payload.cacheHit,
                });
            } catch (err) {
                for (const id of toFetch) _windowLoadedAt.delete(id);
                if (mySeq === _windowEnsureSeq) {
                    log.warn('epg.window_channels_failed', {
                        count: toFetch.length,
                        error: (err as Error).message,
                    });
                }
            }
        });
        await _windowFlushPromise;
    }

    /**
     * Drop everything loaded for `liveId` from the window AND guide
     * accumulations, then refetch its window EPG. Call after a mutation that
     * changes the channel's EPG mapping (the `custom_epg_channel_id` override
     * picker) — post-4.10 rule #7: a cross-store mutation ships an explicit
     * downstream-refetch signal. Without it, the old mapping's programmes
     * linger in the merged index AND the per-id loaded-stamps suppress any
     * refetch for REFRESH_INTERVAL_MS — the new programme only appeared once
     * the user happened to tune the channel (loadWindow uses a separate
     * staleness key).
     */
    async function refetchWindowChannel(liveId: string): Promise<void> {
        if (!liveId) return;
        windowChannels.value = windowChannels.value.filter(
            (ch) => `${ch.liveSourceId}:${ch.liveItemId}` !== liveId,
        );
        windowProgrammes.value = windowProgrammes.value.filter((p) => p.liveId !== liveId);
        _rebuildWindowIndex();
        _windowLoadedAt.delete(liveId);
        // Guide accumulation too — the next Guide ensure refetches this id
        // with the new mapping instead of serving the stale bucket.
        guideChannels.value = guideChannels.value.filter(
            (ch) => `${ch.liveSourceId ?? ch.sourceId}:${ch.liveItemId ?? ch.id}` !== liveId,
        );
        guideProgrammes.value = guideProgrammes.value.filter((p) => p.liveId !== liveId);
        // R1 review Blocker 1 — the persistent merge-dedupe sets are NOT
        // rebuilt from `guideChannels`/`guideProgrammes` any more (rebuilding
        // them is exactly the O(N) rescan Batch 1 removed), so the filters
        // above do nothing to them on their own. Without this, the very next
        // `ensureGuideChannels` fetch for `liveId` (this function's whole
        // purpose — pick up the NEW EPG mapping) arrives with the SAME
        // channel key and the SAME-or-overlapping programme identity tuples,
        // both already marked "seen" from before the purge:
        // `_mergeGuidePayload` would silently drop every row it returns and
        // the channel would render blank until an unrelated full-window
        // clear happened to reset the sets. Remove exactly the keys this
        // liveId owns — the channel key is `liveId` itself; the programme
        // keys are derived from `_guideByLiveId`'s bucket BEFORE it's cleared
        // below, via the SAME `_guideProgrammeKey` builder `_mergeGuidePayload`
        // uses (so the two can never compute the key differently).
        _guideSeenChannelKeys.delete(liveId);
        for (const p of _guideByLiveId.get(liveId) ?? []) {
            _guideSeenProgrammeKeys.delete(_guideProgrammeKey(p.liveId, p.startMs, p.stopMs));
        }
        _guideByLiveId.delete(liveId);
        _guideLoadedLiveIds.delete(liveId);
        // Guide-row-status Batch 2 — a mapping-override save is a fresh
        // start for this channel; a failed-fetch marker from before the
        // mapping changed must not linger and paint the retry treatment
        // over a channel nothing has attempted to fetch since.
        _guideFailedLiveIds.delete(liveId);
        await ensureWindowChannels([liveId]);
    }

    /**
     * Fetch the EPG window from every enabled source of type `xtream` or `epg`,
     * concatenate, **dedup channels by `(sourceId, tvgId)`** (`[NEW]` improves
     * on legacy concat at `EpgGuide.js:268-275`), and rebuild the lookup indices.
     *
     * `force` is currently a no-op signal — the server endpoint hardcodes a
     * ±24h window and has no `?refresh=1` cache-bust on this route. We pass
     * the flag through so the Guide page can hint that the user pressed
     * "Refresh EPG" without changing semantics.
     */
    async function load(force = false): Promise<void> {
        // Stale-time guard. Multiple consumers (Live sidebar, Guide page,
        // background refresh) all call load() — without this, route
        // transitions and component remounts re-fetch the entire EPG every
        // time. The 5-minute window matches REFRESH_INTERVAL_MS so the
        // background poller is the canonical source of freshness.
        if (
            !force
            && lastLoadedAt.value > 0
            && Date.now() - lastLoadedAt.value < REFRESH_INTERVAL_MS
        ) {
            return;
        }
        const mySeq = ++_loadSeq.value;
        isLoading.value = true;
        try {
            const allSources = await api.sources.getAll();
            const targets = allSources.filter((s) => s.enabled !== false && (s.type === 'xtream' || s.type === 'epg'));
            if (targets.length === 0) {
                if (mySeq === _loadSeq.value) {
                    channels.value = [];
                    programmes.value = [];
                    _invalidateEpgIndex();
                    lastLoadedAt.value = Date.now();
                }
                return;
            }
            const responses: Array<{ sourceId: number; payload: EpgGetResponse }> = [];
            await Promise.all(
                targets.map(async (s) => {
                    try {
                        const payload = await api.proxy.epg.get(s.id);
                        responses.push({ sourceId: s.id, payload });
                    } catch (err) {
                        log.warn('epg.source_failed', { sourceId: s.id, error: (err as Error).message });
                    }
                }),
            );
            // A newer load() superseded us — abandon results.
            if (mySeq !== _loadSeq.value) return;

            // Dedup channels by `(sourceId, tvgId)`. Legacy concats without dedup which
            // double-renders the same channel when two XMLTV exports overlap.
            // Programmes are also deduped by `(sourceId, channelId, startMs)` —
            // post-4.10 hotfix #10. Real-world XMLTV feeds occasionally emit
            // the same `<programme>` twice (data-quality glitches, especially
            // around schedule revisions), AND the server's `?channelIds=a,a,b`
            // path can land the same upstream row in multiple matches under
            // some upstream-driver shapes. Without dedup the same programme
            // appeared twice in a row's bucket → Vue's keyed v-for warning
            // ("Duplicate keys found during update") → keyed-list reconciler
            // got confused → stale renders that only refreshed when the user
            // toggled an unrelated filter to force a recompute.
            const seenChannelKey = new Set<string>();
            const seenProgrammeKey = new Set<string>();
            const mergedChannels: EpgChannelWithSource[] = [];
            const mergedProgrammes: ProgrammeWithSource[] = [];
            // markRaw on each row — defense in depth alongside the
            // shallowRef holders. The 100k+ programme catalog is the
            // single most allocation-sensitive surface in the app.
            for (const { sourceId, payload } of responses) {
                for (const ch of payload.channels ?? []) {
                    const k = `${sourceId}:${ch.id}`;
                    if (seenChannelKey.has(k)) continue;
                    seenChannelKey.add(k);
                    mergedChannels.push(markRaw({ ...ch, sourceId }));
                }
                for (const p of payload.programmes ?? []) {
                    const startMs = parseTimestamp(p.start);
                    const stopMs = parseTimestamp(p.stop);
                    if (!Number.isFinite(startMs) || !Number.isFinite(stopMs)) continue;
                    // Dedup key includes BOTH startMs AND stopMs so two
                    // programmes that share a start but have different
                    // durations (one is a longer-cut, one is short) stay
                    // distinct — the resolver's "longest programme wins"
                    // tie-break depends on both being available.
                    const pk = `${sourceId}:${p.channelId}:${startMs}:${stopMs}`;
                    if (seenProgrammeKey.has(pk)) continue;
                    seenProgrammeKey.add(pk);
                    mergedProgrammes.push(markRaw({ ...p, sourceId, startMs, stopMs }));
                }
            }

            channels.value = mergedChannels;
            programmes.value = mergedProgrammes;
            _invalidateEpgIndex();
            _rebuildIndex();
            lastLoadedAt.value = Date.now();
            log.info('epg.loaded', {
                sources: responses.length,
                channels: mergedChannels.length,
                programmes: mergedProgrammes.length,
                force,
            });
        } catch (err) {
            log.warn('epg.load_failed', { error: (err as Error).message });
        } finally {
            if (mySeq === _loadSeq.value) isLoading.value = false;
        }
    }

    /**
     * Look up the now-playing programme for a channel. `tvgId` is the primary
     * key; `channelName` is the fallback when XMLTV omits `tvg-id` (legacy
     * `EpgGuide.js:308-343`). Cached per-(programmes.length) — the cache
     * rebuilds whenever the programme list grows or shrinks.
     */
    function getCurrentProgram(
        tvgId: string | null | undefined,
        channelName: string | null | undefined,
        nowMs: number = Date.now(),
    ): ProgrammeWithSource | null {
        // Refresh cache key proxy (`programmes.length`) lazily.
        if (_nowPlayingKey !== programmes.value.length) {
            _nowPlayingCache = new Map();
            _nowPlayingKey = programmes.value.length;
        }
        const cacheKey = `${tvgId ?? ''}::${normalizeChannelName(channelName ?? '')}::${Math.floor(nowMs / 60_000)}`;
        const hit = _nowPlayingCache.get(cacheKey);
        if (hit !== undefined) return hit;
        const programme = getProgramAt(tvgId, channelName, nowMs);
        _nowPlayingCache.set(cacheKey, programme);
        return programme;
    }

    function getCurrentProgramForLiveId(
        liveId: string | null | undefined,
        nowMs: number = Date.now(),
    ): ProgrammeWithSource | null {
        // Reactive touch: `_byLiveId` is a plain Map (not reactive), rebuilt from
        // `windowProgrammes` in the same `applyWindowPayload` tick. Reading the
        // shallowRef here makes any consumer COMPUTED (player tune-in card, Live
        // now/next panel, ChannelTile inline EPG) re-run when the window loads —
        // without it they read a stale/empty index once and never update after
        // the async fetch resolves. (This was the "EPG missing on Live" bug.)
        void windowProgrammes.value;
        if (!liveId) return null;
        const rows = _byLiveId.get(liveId) || [];
        let best: ProgrammeWithSource | null = null;
        let bestDuration = -1;
        for (const p of rows) {
            if (p.startMs <= nowMs && nowMs < p.stopMs) {
                const duration = p.stopMs - p.startMs;
                if (duration > bestDuration) {
                    best = p;
                    bestDuration = duration;
                }
            }
        }
        return best;
    }

    /**
     * The next programme starting at/after `nowMs` for a live channel, from the
     * loaded window (`_byLiveId`). Powers the Live now/next panel's NEXT on the
     * live page (where the main `programmes` index `getProgramAt` reads is empty
     * — only the window is loaded). Reactive via the `windowProgrammes` touch.
     */
    function getNextProgramForLiveId(
        liveId: string | null | undefined,
        nowMs: number = Date.now(),
    ): ProgrammeWithSource | null {
        void windowProgrammes.value;
        if (!liveId) return null;
        const rows = _byLiveId.get(liveId) || [];
        let next: ProgrammeWithSource | null = null;
        for (const p of rows) {
            if (p.startMs >= nowMs && (!next || p.startMs < next.startMs)) next = p;
        }
        return next;
    }

    /**
     * The programmes starting at/after `fromMs` for a live channel, ascending
     * by start time, capped at `limit`. Powers Live's "Up next on this channel"
     * list at the large-mobile tier (mobile-foldable-redesign §2.3) — a pure
     * read over the window `Live.vue` already loads via `ensureWindowChannels`,
     * so no new endpoint and no new fetch.
     *
     * DEDUPED by the full identity tuple `(sourceId, channelId, startMs,
     * stopMs)` — root rule #4. The window index is a merge of per-source
     * payloads and real Xtream/XMLTV data carries duplicate `<programme>`
     * entries; a repeated row here would give two list items the same `:key`
     * and render the same programme twice under "Up next". Two airings of the
     * same title at DIFFERENT times are not duplicates and both survive.
     *
     * Reactive via the `windowProgrammes` touch, same as its siblings above.
     */
    function getWindowProgrammesForLiveId(
        liveId: string | null | undefined,
        fromMs: number = Date.now(),
        limit = 4,
    ): ProgrammeWithSource[] {
        void windowProgrammes.value;
        if (!liveId || limit <= 0) return [];
        const rows = _byLiveId.get(liveId) || [];
        const seen = new Set<string>();
        const out: ProgrammeWithSource[] = [];
        for (const p of rows) {
            if (p.startMs < fromMs) continue;
            const key = `${p.sourceId}|${p.channelId}|${p.startMs}|${p.stopMs}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(p);
        }
        out.sort((a, b) => a.startMs - b.startMs || a.stopMs - b.stopMs);
        return out.slice(0, limit);
    }

    function hasEpgForLiveId(liveId: string | null | undefined): boolean {
        void windowProgrammes.value; // reactive touch — see getCurrentProgramForLiveId
        return !!liveId && (_byLiveId.get(liveId)?.length ?? 0) > 0;
    }

    /**
     * Look up the programme covering `ts` for the given channel. Four-tier
     * resolution (post-4.10):
     *
     *   1. tvg-id strict match  (`_byTvgId`).
     *   2. Strict-prefix match  (`${livePrefix}:${bareKey}` in `_byPrefixedName`).
     *   3. Alias-prefix match   (each alias from `expandPrefixAliases`).
     *   4. N=1 bare-name gate   (lookup `bareKey` in `_byBareName`; resolve
     *                            only when EXACTLY ONE distinct channel
     *                            matches AND has a programme covering `ts`).
     *
     * When multiple candidates cover the same instant in any tier (cross-
     * source overlap, near-overlapping schedules), pick the LONGEST
     * programme — most-specific wins. Tie-break by lowest `sourceId` for
     * deterministic ordering across reloads.
     *
     * Returns `null` when none of the four tiers resolves. The Phase C
     * `custom_epg_channel_id` per-channel override is the user-facing
     * escape hatch for the residual ambiguous-N>1 cases.
     */
    function getProgramAt(
        tvgId: string | null | undefined,
        channelName: string | null | undefined,
        ts: number,
    ): ProgrammeWithSource | null {
        const candidates: ProgrammeWithSource[] = [];
        // Tier 1: tvg-id strict.
        if (tvgId) {
            const byId = _byTvgId.get(tvgId);
            if (byId) candidates.push(...byId);
        }
        // Tier 2 + 2.5: strict-prefix → alias-prefix.
        if (candidates.length === 0 && channelName) {
            const bareKey = normalizeChannelNameWithoutPrefix(channelName);
            if (bareKey) {
                const livePrefix = extractCountryPrefix(channelName) || '';
                if (livePrefix) {
                    // Try the live prefix first (strict-prefix), then each
                    // alias in order — `expandPrefixAliases` always returns
                    // the live prefix as the first entry.
                    for (const prefix of expandPrefixAliases(livePrefix)) {
                        const hit = _byPrefixedName.get(`${prefix}:${bareKey}`);
                        if (hit && hit.length > 0) {
                            candidates.push(...hit);
                            break;
                        }
                    }
                }
                // If still nothing, try the no-prefix bucket — channels
                // whose name had no colon-form prefix at all (e.g. plain
                // `BBC One` from a Phase A canonical-group EPG source).
                if (candidates.length === 0) {
                    const hit = _byPrefixedName.get(`:${bareKey}`);
                    if (hit && hit.length > 0) candidates.push(...hit);
                }
                // Tier 3: N=1 bare-name gate.
                if (candidates.length === 0) {
                    const bareChannels = _byBareName.get(bareKey) || [];
                    // Dedup by (sourceId, channelId) so multiple programmes
                    // on the same channel don't inflate the candidate count.
                    const distinct = new Set(bareChannels.map((c) => `${c.sourceId}:${c.id}`));
                    if (distinct.size === 1) {
                        const only = bareChannels[0]!;
                        const programmesForOnly = _byTvgId.get(only.id);
                        if (programmesForOnly) candidates.push(...programmesForOnly);
                    }
                }
            }
        }
        let best: ProgrammeWithSource | null = null;
        let bestDuration = -1;
        for (const p of candidates) {
            if (p.startMs <= ts && ts < p.stopMs) {
                const duration = p.stopMs - p.startMs;
                if (
                    duration > bestDuration
                    || (duration === bestDuration
                        && (best === null || p.sourceId < best.sourceId))
                ) {
                    best = p;
                    bestDuration = duration;
                }
            }
        }
        return best;
    }

    /**
     * Resolve a live channel's `(tvgId, channelName)` to the `(sourceId,
     * channelId)` tuple of the EPG channel whose programmes the 4-tier
     * resolver would surface. Mirrors `getProgramAt`'s tier shape EXCEPT it
     * returns the route identifier instead of a single programme — used by
     * the Guide grid so each rendered live row can bucket programmes via the
     * correct `(sourceId, channelId)` key.
     *
     * Without this helper the grid bucket-key (`${sourceId}:${channelId}`)
     * only matches when the live row's `id` is the literal tvg-id from
     * `data.epg_channel_id` / `data.tvgId`. Live channels with no tvg-id
     * (the dominant case on real Xtream playlists) get a synthetic
     * `live:<itemId>` row id that never collides with any programme's
     * `channelId` — so the row renders empty, even when the coverage
     * indicator (which uses `getProgramAt`) reports a match. This helper
     * closes the gap.
     *
     * Returns `null` when none of the four tiers resolves.
     */
    function findEpgRouteFor(
        tvgId: string | null | undefined,
        channelName: string | null | undefined,
    ): { sourceId: number; channelId: string } | null {
        // Tier 1 — tvg-id strict. If the live row carries a tvg-id AND we
        // have programmes for it, the channel itself sits under one of the
        // dedupe buckets in `channels` keyed by `(sourceId, tvgId)`. We
        // search by tvgId across channels rather than picking the first
        // programme's `sourceId` because the same tvg-id can appear in
        // multiple sources (cross-source overlap).
        if (tvgId) {
            const byId = _byTvgId.get(tvgId);
            if (byId && byId.length > 0) {
                const first = byId[0]!;
                return { sourceId: first.sourceId, channelId: tvgId };
            }
        }
        if (!channelName) return null;
        const bareKey = normalizeChannelNameWithoutPrefix(channelName);
        if (!bareKey) return null;
        const livePrefix = extractCountryPrefix(channelName) || '';

        // Tier 2 + 2.5 — strict-prefix → alias-prefix. The matching EPG
        // channel's `(sourceId, id)` is recoverable from the first programme
        // in the prefixed bucket (every programme in the bucket carries the
        // same channel route — the bucket was built by joining the EPG
        // channel's `id` to `_byTvgId.get(id)`).
        if (livePrefix) {
            for (const prefix of expandPrefixAliases(livePrefix)) {
                const hit = _byPrefixedName.get(`${prefix}:${bareKey}`);
                if (hit && hit.length > 0) {
                    const first = hit[0]!;
                    return { sourceId: first.sourceId, channelId: first.channelId };
                }
            }
        }
        // No-prefix bucket.
        const noPrefixHit = _byPrefixedName.get(`:${bareKey}`);
        if (noPrefixHit && noPrefixHit.length > 0) {
            const first = noPrefixHit[0]!;
            return { sourceId: first.sourceId, channelId: first.channelId };
        }

        // Tier 3 — N=1 bare-name gate.
        const bareChannels = _byBareName.get(bareKey) || [];
        const distinct = new Set(bareChannels.map((c) => `${c.sourceId}:${c.id}`));
        if (distinct.size === 1) {
            const only = bareChannels[0]!;
            return { sourceId: only.sourceId, channelId: only.id };
        }
        return null;
    }

    /**
     * Start the 5-minute background refresh loop. Idempotent — calling twice
     * is a no-op. Guide page invokes on mount, stops on unmount; legacy bug
     * was leaking this timer when the Guide page went off-screen.
     *
     * Post-4.10 §A.6 — re-runs the last window payload (`_lastWindow`) when
     * set, falling back to bare `load(false)` for legacy consumers
     * (`<EpgMappingPicker>`). This prevents the window-scoped Live/Guide path
     * from silently reverting to the full-catalogue shape every 5 min on
     * large playlists.
     *
     * Phase 1 §A — also refetches the currently-loaded Guide set when there is
     * an active guide window. This is the "refetch the guide's loaded set for
     * the active window" requirement from the spec. Only the ids already in
     * `_guideLoadedLiveIds` are re-fetched (not the full catalogue), so the
     * background tick stays bounded even on "Group: All".
     */
    function startBackgroundRefresh(): void {
        if (_refreshTimer !== null) return;
        _refreshTimer = setInterval(() => {
            // Guide window background refresh — re-fetch only the currently-loaded
            // guide set so the Guide page stays fresh without the full catalogue.
            if (_guideWindow !== null && _guideLoadedLiveIds.size > 0) {
                const ids = [..._guideLoadedLiveIds];
                const { startMs, endMs } = _guideWindow;
                // Reset the loaded set so ensureGuideChannels will re-fetch them,
                // then clear accumulated data and refetch. Re-fetches the SAME
                // already-fetched (padded) span — `_guideFetchedSpan` is
                // unchanged in VALUE, just re-set alongside `_guideWindow` so
                // the two never drift apart.
                _clearGuideState();
                _guideWindow = { startMs, endMs };
                _guideFetchedSpan = { startMs, endMs };
                // Chunked + sequential (R1 review Blocker 4 audit — a
                // pre-existing gap adjacent to `_extendGuideWindow`'s, found
                // while fixing that one, not introduced by it): `ids` is
                // `_guideLoadedLiveIds` in full, which accumulates across an
                // entire session, unlike every OTHER caller of
                // `ensureGuideChannels` — an unchunked refresh tick risks the
                // exact same 413 the delta-fetch fix guards against.
                // Sequential awaits (not Promise.all) keep each chunk's own
                // `_guidePendingIds` flush from coalescing back into one
                // oversized request.
                void (async () => {
                    for (let i = 0; i < ids.length; i += GUIDE_FETCH_CAP) {
                        await ensureGuideChannels(ids.slice(i, i + GUIDE_FETCH_CAP));
                    }
                })();
            } else if (_lastWindow !== null) {
                void loadWindow(_lastWindow);
            } else {
                void load(false);
            }
        }, REFRESH_INTERVAL_MS);
    }

    /** Stop the background refresh. Idempotent. */
    function stopBackgroundRefresh(): void {
        if (_refreshTimer === null) return;
        clearInterval(_refreshTimer);
        _refreshTimer = null;
    }

    function reset(): void {
        channels.value = [];
        programmes.value = [];
        windowChannels.value = [];
        windowProgrammes.value = [];
        lastLoadedAt.value = 0;
        isLoading.value = false;
        _invalidateEpgIndex();
        _invalidateWindowIndex();
        stopBackgroundRefresh();
        _lastWindow = null;
        _filterStaleAt.clear();
        // Drain the window-ensure accumulation (sidebar inline-EPG path).
        _windowLoadedAt.clear();
        _windowPendingIds = new Set();
        _windowEnsureSeq++;
        // Also drain the Guide-scoped accumulation so a full store reset is
        // self-contained (covers test afterEach drains + page-level teardown).
        _clearGuideState();
        _guideWindow = null;
        _guideFetchedSpan = null;
        guideHorizonProgrammes.value = [];
        // Notifier-scoped (Spec 10) — drained with everything else so a full
        // store reset is self-contained.
        notifierProgrammes.value = [];
        notifierChannelNames.value = new Map();
    }

    return {
        channels,
        programmes,
        windowChannels,
        windowProgrammes,
        /** Guide-scoped accumulated channels (Phase 1 §A D3). */
        guideChannels,
        /** Guide-scoped accumulated programmes (Phase 1 §A D3). */
        guideProgrammes,
        /** Phase X3 — multi-day EPG-availability probe (U1 day strip). */
        guideHorizonProgrammes,
        /** Spec 10 — EPG-start-notification-scoped programmes (see `loadNotifierWindow`). */
        notifierProgrammes,
        /** Spec 10 — `liveId` → channel name from the same payload (names are not on programme rows). */
        notifierChannelNames,
        lastLoadedAt,
        isLoading,
        load,
        loadWindow,
        ensureWindowChannels,
        invalidateWindowChannels,
        refetchWindowChannel,
        getCurrentProgram,
        getCurrentProgramForLiveId,
        getNextProgramForLiveId,
        getWindowProgrammesForLiveId,
        hasEpgForLiveId,
        getProgramAt,
        findEpgRouteFor,
        // Guide-scoped window methods (Phase 1 §A D3):
        setGuideWindow,
        ensureGuideChannels,
        hasGuideEpgForLiveId,
        isGuideChannelLoaded,
        isGuideChannelFailed,
        guideProgrammesFor,
        resetGuideWindow,
        loadGuideHorizon,
        loadNotifierWindow,
        guideDayNavStartTimes,
        startBackgroundRefresh,
        stopBackgroundRefresh,
        reset,
    };
});

/** Parse a Programme timestamp. Server returns ISO-8601 strings; numeric fallbacks tolerated. */
function parseTimestamp(raw: string | number): number {
    if (typeof raw === 'number') return raw;
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
    const asNum = Number(raw);
    return Number.isFinite(asNum) ? asNum : NaN;
}
